// src/components/Checkout/CartSummary.tsx

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./Checkout.module.scss";
import { getSiteConfig } from "@/config/siteConfigs";

type OrderType = "exchpart" | "sellpart";

const CRM_TOKEN = "DH345DFGDGb32FDsf03876SDFSdf13";

interface CartSummaryProps {
  cart: Array<{
    goid: string;
    quantity: number;
    table: string;
    typeorder: OrderType;
  }>;
}

export const CartSummary: React.FC<CartSummaryProps> = ({ cart }) => {
  const { t } = useTranslation("common");
  const siteConfig = getSiteConfig();
  const [totalAmountNet, setTotalAmountNet] = useState(0);
  const [totalAmountVat, setTotalAmountVat] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAllProducts = async () => {
      setLoading(true);
      let totalNet = 0;
      let totalWithVat = 0;
      let itemsCount = 0;

      const promises = cart.map(async (item) => {
        try {
          const response = await fetch(
            `https://crm.hixa.eu/external/goodsapi/?legid=1&language=${siteConfig.language}&type=good&goid=${item.goid}&table=${item.table}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${CRM_TOKEN}`,
                Accept: "application/json",
              },
            },
          );

          if (response.ok) {
            const data = await response.json();
            if (data.data && data.data.length > 0) {
              const productData = data.data[0];
              const priceNet =
                item.typeorder === "sellpart"
                  ? parseFloat(productData.nettoprice)
                  : parseFloat(productData.priceexch);

              const priceWithVat =
                item.typeorder === "sellpart"
                  ? parseFloat(productData.pricevat)
                  : parseFloat(productData.priceexchvat);

              return {
                priceNet: priceNet || 0,
                priceWithVat: priceWithVat || 0,
                quantity: item.quantity,
              };
            }
          }
        } catch (error) {
          console.error(`Error fetching product ${item.goid}:`, error);
        }
        return { priceNet: 0, priceWithVat: 0, quantity: 0 };
      });

      const results = await Promise.all(promises);

      results.forEach((result) => {
        totalNet += result.priceNet * result.quantity;
        totalWithVat += result.priceWithVat * result.quantity;
        itemsCount += result.quantity;
      });

      setTotalAmountNet(totalNet);
      setTotalAmountVat(totalWithVat);
      setTotalItems(itemsCount);
      setLoading(false);
    };

    if (cart.length > 0) {
      fetchAllProducts();
    } else {
      setTotalAmountNet(0);
      setTotalAmountVat(0);
      setTotalItems(0);
      setLoading(false);
    }
  }, [cart, siteConfig.language]);

  if (loading && cart.length > 0) {
    return (
      <div className={styles.summary}>
        <p>{t("cart.loadingSummary")}</p>
      </div>
    );
  }

  const vatAmount = totalAmountVat - totalAmountNet;

  return (
    <>
      <li className={styles.summary}>
        <p>
          {t("cart.subtotal")} · {totalItems} {t("cart.items")} ·
        </p>
        <p>{totalAmountNet.toFixed(2)}€</p>
      </li>
      <li className={styles.summary}>
        <p>{t("cart.vat")}</p>
        <p>{vatAmount.toFixed(2)}€</p>
      </li>
      <li className={styles.summary}>
        <p className={styles.total}>{t("cart.totalWithVat")}</p>
        <div className={styles.totalPriceBlock}>
          <p className={styles.fs12}>EUR </p>
          <p className={styles.totalPrice}>{totalAmountVat.toFixed(2)}€</p>
        </div>
      </li>
    </>
  );
};
